import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  startDeletingCatalogNameRow,
  startLoadingCatalogNameRows,
  startSavingCatalogNameRow
} from "../../app/actions/catalogTopicQuestionActions";

const emptyForm = { name: "", description: "" };

const AdminCatalogNameScreen = ({ kind = "topic" }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const titleKey = kind === "question" ? "admin.catalogQuestion.title" : "admin.catalogTopic.title";
  const titleFallback = kind === "question" ? "Preguntas" : "Tópicos";

  const load = async () => {
    setLoading(true);
    const next = await dispatch(startLoadingCatalogNameRows(kind));
    setRows(next || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [kind]);

  const visible = useMemo(() => {
    const fold = (value) => String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const query = fold(text);
    if (!query) {
      return rows;
    }
    return rows.filter((row) => fold(`${row.id} ${row.name} ${row.description}`).includes(query));
  }, [rows, text]);

  const reset = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      Swal.fire(t("titles.error"), t("admin.catalogTopic.nameRequired", "El nombre es obligatorio"), "error");
      return;
    }
    const saved = await dispatch(startSavingCatalogNameRow(kind, {
      name: form.name.trim(),
      description: form.description.trim() || form.name.trim()
    }, editingId));
    if (saved) {
      reset();
      load();
    }
  };

  const remove = async (row) => {
    const confirmed = await Swal.fire({
      title: t("buttons.delete", "Eliminar"),
      text: row.name,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("buttons.delete", "Eliminar"),
      cancelButtonText: t("buttons.cancel", "Cancelar")
    });
    if (!confirmed.isConfirmed) {
      return;
    }
    const ok = await dispatch(startDeletingCatalogNameRow(kind, row.id));
    if (ok) {
      if (Number(editingId) === Number(row.id)) {
        reset();
      }
      load();
    }
  };

  return (
    <div className="container py-4">
      <h3 className="mb-3">{t(titleKey, titleFallback)}</h3>
      <form className="card card-body mb-4" onSubmit={submit}>
        <div className="row g-2">
          <div className="col-md-4">
            <label className="form-label">{t("labels.name", "Nombre")}</label>
            <input
              className="form-control"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            />
          </div>
          <div className="col-md-5">
            <label className="form-label">{t("labels.description", "Descripción")}</label>
            <input
              className="form-control"
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            />
          </div>
          <div className="col-md-3 d-flex align-items-end gap-2">
            <button type="submit" className="btn btn-primary">
              {editingId ? t("buttons.update", "Actualizar") : t("buttons.add", "Agregar")}
            </button>
            {editingId && (
              <button type="button" className="btn btn-outline-secondary" onClick={reset}>
                {t("buttons.cancel", "Cancelar")}
              </button>
            )}
          </div>
        </div>
      </form>

      <input
        className="form-control mb-3"
        placeholder={t("general.search", "Buscar")}
        value={text}
        onChange={(event) => setText(event.target.value)}
      />

      {loading && <p className="text-muted">{t("labels.loading", "Cargando...")}</p>}

      <div className="table-responsive">
        <table className="table table-sm table-hover">
          <thead>
            <tr>
              <th>Id</th>
              <th>{t("labels.name", "Nombre")}</th>
              <th>{t("labels.description", "Descripción")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.id}>
                <td>{row.id}</td>
                <td>{row.name}</td>
                <td>{row.description}</td>
                <td className="text-end">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary me-2"
                    onClick={() => {
                      setEditingId(row.id);
                      setForm({ name: row.name || "", description: row.description || "" });
                    }}
                  >
                    {t("buttons.edit", "Editar")}
                  </button>
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => remove(row)}>
                    {t("buttons.delete", "Eliminar")}
                  </button>
                </td>
              </tr>
            ))}
            {!loading && !visible.length && (
              <tr>
                <td colSpan={4} className="text-center text-muted py-4">
                  {t("table.loading", "No se encontraron resultados...")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCatalogNameScreen;
